import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { ShoppingCart, Trash2, ArrowRight, Plus, Minus, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const { cart, removeFromCart, loading, fetchCart } = useCart();
  const { isAuthenticated, getAuthHeaders, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const verifyAuth = async () => {
      await checkAuth();
      setAuthChecked(true);
    };
    verifyAuth();
  }, [checkAuth]);

  const handleRemove = async (productId) => {
    const result = await removeFromCart(productId);
    if (result.success) {
      toast.success('Item removed from cart');
    } else {
      toast.error(result.error);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to checkout');
      navigate('/auth');
      return;
    }

    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: window.location.origin
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D4A5E]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag className="w-20 h-20 mx-auto text-[#CBD5E1] mb-6" />
            <h1 className="text-2xl font-bold text-[#1A202C] mb-4">Sign in to view your cart</h1>
            <p className="text-[#64748B] mb-8">Please login or create an account to add items to your cart.</p>
            <Link to="/auth">
              <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-8 py-6" data-testid="cart-login-btn">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#2D4A5E] flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Your Cart</h1>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white h-32 rounded-xl"></div>
            ))}
          </div>
        ) : !cart.items || cart.items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <ShoppingBag className="w-20 h-20 mx-auto text-[#CBD5E1] mb-6" />
            <h2 className="text-2xl font-bold text-[#1A202C] mb-4">Your cart is empty</h2>
            <p className="text-[#64748B] mb-8">Looks like you haven't added anything yet.</p>
            <Link to="/courses">
              <Button className="bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full px-8 py-6" data-testid="browse-courses-btn">
                Browse Courses
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => (
                <div 
                  key={item.product_id} 
                  className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-[#E2E8F0] flex gap-4"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img
                    src={item.product?.thumbnail || 'https://images.unsplash.com/photo-1634117622592-114e3024ff27?w=200'}
                    alt={item.product?.title}
                    className="w-24 h-24 sm:w-32 sm:h-24 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-4">
                      <div>
                        <Link 
                          to={`/product/${item.product_id}`}
                          className="font-semibold text-[#1A202C] hover:text-[#2D4A5E] transition-colors line-clamp-2"
                        >
                          {item.product?.title}
                        </Link>
                        <p className="text-sm text-[#64748B] mt-1">{item.product?.category}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-[#2D4A5E]">
                          ${item.price?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-[#64748B]">
                        {item.product_type === 'course' ? 'Course' : 'Digital Download'}
                      </div>
                      <button
                        onClick={() => handleRemove(item.product_id)}
                        className="text-red-500 hover:text-red-600 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0] sticky top-24">
                <h2 className="text-xl font-bold text-[#1A202C] mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-[#64748B]">
                    <span>Subtotal ({cart.items.length} items)</span>
                    <span>${cart.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#64748B]">
                    <span>Tax</span>
                    <span>$0.00</span>
                  </div>
                </div>

                <div className="border-t border-[#E2E8F0] pt-4 mb-6">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-[#2D4A5E]">${cart.total?.toFixed(2)}</span>
                  </div>
                </div>

                <Button 
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="w-full bg-[#2D4A5E] hover:bg-[#243B4A] text-white rounded-full py-6 text-lg btn-hover-lift"
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    <>
                      Proceed to Checkout
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-center text-[#64748B] mt-4">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
